import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';
import type { FilterSpecification } from 'maplibre-gl';
import type { FeatureCollection, Geometry } from 'geojson';

import type { Client } from '@/clients/types';
import type { FeatureOverlayState } from '@/components/map/utils/mapLayers';
import { addOrbitLayer, ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { fetchSurveyedAreasForClient } from '@/api/surveyedAreasApi';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from '@/utils/mapUtils';
import { showErrorToast } from '@/errors/showErrorToast';

export const SURVEYED_AREAS_SOURCE_ID = 'orbit-surveyed-areas-source';

// Surveyed areas are a small, bounded per-client dataset that must stay visible across the
// whole Network Overview band — including further out than a single viewport would allow.
// A viewport bbox at those wider scales exceeds the backend's 2-square-degree limit, so we
// load the client's areas in one shot (GET /areas) instead of per-viewport, and rely on the
// semantic-zoom system to confine rendering to the Network Overview level.
function registerSurveyedAreasSourceAndLayers(mlMap: maplibregl.Map, data: FeatureCollection, visible: boolean): void {
    const visibility = visible ? 'visible' : 'none';

    try {
        if (mlMap.getSource(SURVEYED_AREAS_SOURCE_ID)) {
            (mlMap.getSource(SURVEYED_AREAS_SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
        } else {
            mlMap.addSource(SURVEYED_AREAS_SOURCE_ID, { type: 'geojson', data, promoteId: 'name' });
        }
    } catch {
        return;
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.surveyedAreasFill)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.surveyedAreasFill,
                    type: 'fill',
                    source: SURVEYED_AREAS_SOURCE_ID,
                    layout: { visibility },
                    paint: {
                        'fill-color': '#007CFF',
                        'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.35, 0.2],
                    },
                },
                'areaOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.surveyedAreasOutline)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.surveyedAreasOutline,
                    type: 'line',
                    source: SURVEYED_AREAS_SOURCE_ID,
                    layout: { visibility },
                    paint: {
                        'line-color': '#007CFF',
                        'line-width': ['case', ['boolean', ['feature-state', 'hover'], false], 2.5, 1.5],
                        'line-dasharray': [3, 3],
                    },
                },
                'areaOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.surveyedAreasSelectedFill)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.surveyedAreasSelectedFill,
                    type: 'fill',
                    source: SURVEYED_AREAS_SOURCE_ID,
                    layout: { visibility },
                    filter: ['==', ['get', 'name'], ''] as FilterSpecification,
                    paint: {
                        'fill-color': '#1d4ed8',
                        'fill-opacity': 0.35,
                    },
                },
                'areaOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.surveyedAreasSelectedOutline)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.surveyedAreasSelectedOutline,
                    type: 'line',
                    source: SURVEYED_AREAS_SOURCE_ID,
                    layout: { visibility },
                    filter: ['==', ['get', 'name'], ''] as FilterSpecification,
                    paint: {
                        'line-color': '#1e40af',
                        'line-width': 3,
                        'line-opacity': 1,
                    },
                },
                'areaOverlays',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.areaLabels)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.areaLabels,
                    type: 'symbol',
                    source: SURVEYED_AREAS_SOURCE_ID,
                    layout: {
                        visibility,
                        'text-field': ['get', 'name'],
                        // OpenFreeMap's glyphs endpoint hosts "Noto Sans …"; without an explicit
                        // text-font MapLibre requests its default stack, which 404s.
                        'text-font': ['Noto Sans Regular'],
                        'text-size': 14,
                        'text-anchor': 'center',
                        'text-max-width': 8,
                        'text-allow-overlap': false,
                        'text-ignore-placement': false,
                    },
                    paint: {
                        // Origin Blue to match the area fill/outline; white halo for legibility.
                        'text-color': '#007CFF',
                        'text-halo-color': '#ffffff',
                        'text-halo-width': 2,
                    },
                },
                'businessLabels',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }
}

/**
 * Loads all surveyed areas for the selected client in one request (GET /areas), registers the
 * MapLibre GeoJSON source and three layers (fill, outline, labels), and re-registers them after
 * every base map style change. Loading the whole set (rather than per-viewport) keeps areas
 * available across the entire Network Overview band, including zoomed further out than a single
 * viewport bbox could request. Visibility is set at registration time from
 * `featureOverlays.surveyedAreas`; subsequent toggle + semantic-zoom changes are handled by
 * `useMapLayerVisibility` via the populated `surveyedAreas.mapLayerIds` (which confine areas to
 * the Network Overview level).
 *
 * Safe when:
 *   - No client is selected (clears source data, skips fetch).
 *   - Surveyed-area data has not loaded yet (source contains empty FeatureCollection).
 *   - Map layers do not exist (all operations guard with getLayer/getSource).
 *   - The API request fails; the error is surfaced and the map continues working.
 */
export interface SurveyedAreasLayer {
    /** Full unclipped geometry of the loaded surveyed area with this `name`, or null if not loaded. */
    getAreaGeometry: (name: string) => Geometry | null;
    /** The full unclipped surveyed-area collection currently loaded. Stable identity (reads a ref). */
    getAreasData: () => FeatureCollection;
    /** Bumped when the client's areas finish loading — a re-resolve signal for consumers. */
    areasDataVersion: number;
}

export function useSurveyedAreasLayer(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    selectedClient: Client | null,
    featureOverlays: FeatureOverlayState,
    selectedAreaName: string | null = null,
    /**
     * Keeps the area data loaded even when the surveyed-areas overlay is toggled off.
     * Used on Find & Fix where the stats-scope resolver needs the polygons for
     * centre-point containment regardless of rendering visibility.
     */
    keepDataLoaded: boolean = false,
): SurveyedAreasLayer {
    const surveyedAreasDataRef = useRef<FeatureCollection>(EMPTY_FEATURE_COLLECTION);
    const [areasDataVersion, setAreasDataVersion] = useState(0);

    const getAreasData = useCallback((): FeatureCollection => surveyedAreasDataRef.current, []);

    // Look up a loaded surveyed area's full geometry by name. Reads the client data ref
    // (not the tile-clipped rendered features), so callers get the complete polygon. Stable
    // identity — the ref is mutated in place, so this never needs to change.
    const getAreaGeometry = useCallback((name: string): Geometry | null => {
        const feature = surveyedAreasDataRef.current.features.find((f) => f.properties?.name === name);
        return feature?.geometry ?? null;
    }, []);

    // Keep a ref so the registration effect can read the current overlay state
    // without re-running when featureOverlays changes (useMapLayerVisibility
    // owns subsequent visibility toggling via mapLayerIds).
    const featureOverlaysRef = useRef(featureOverlays);
    const selectedAreaNameRef = useRef(selectedAreaName);
    useLayoutEffect(() => {
        featureOverlaysRef.current = featureOverlays;
        selectedAreaNameRef.current = selectedAreaName;
    });

    // Load all surveyed areas for the client once (not per-viewport) when
    // client/map-ready/enabled state changes, so they stay available at every zoom.
    // `keepDataLoaded` keeps the data (not the rendering) alive when the overlay is off.
    const loadData = featureOverlays.surveyedAreas || keepDataLoaded;
    useEffect(() => {
        // The synchronous reset here is deliberately NOT version-bumped (the lint rule
        // forbids sync setState in effects): consumers read live data via getAreasData()
        // and re-resolve on client changes themselves; the version only needs to signal
        // the asynchronous load completion below.
        surveyedAreasDataRef.current = EMPTY_FEATURE_COLLECTION;
        const mlMap = mapRef.current?.getMap();
        if (mlMap) setGeoJsonSourceData(mlMap, SURVEYED_AREAS_SOURCE_ID, EMPTY_FEATURE_COLLECTION);

        if (!selectedClient || !isMapLoaded) return;
        if (!loadData) return;

        let cancelled = false;
        fetchSurveyedAreasForClient(selectedClient.id)
            .then((data) => {
                if (cancelled) return;
                surveyedAreasDataRef.current = data;
                setAreasDataVersion((v) => v + 1);
                const m = mapRef.current?.getMap();
                if (m) setGeoJsonSourceData(m, SURVEYED_AREAS_SOURCE_ID, data);
            })
            .catch((err: unknown) => {
                if (!cancelled) showErrorToast(err);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedClient, isMapLoaded, loadData, mapRef]);

    // Register surveyed-area source and layers when the map is ready, and re-register on
    // every style.load (base map switches reload all sources and layers).
    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const register = () => {
            registerSurveyedAreasSourceAndLayers(
                mlMap,
                surveyedAreasDataRef.current,
                featureOverlaysRef.current.surveyedAreas,
            );
            const filter: FilterSpecification = ['==', ['get', 'name'], selectedAreaNameRef.current ?? ''];
            if (mlMap.getLayer(ORBIT_LAYER_IDS.surveyedAreasSelectedFill))
                mlMap.setFilter(ORBIT_LAYER_IDS.surveyedAreasSelectedFill, filter);
            if (mlMap.getLayer(ORBIT_LAYER_IDS.surveyedAreasSelectedOutline))
                mlMap.setFilter(ORBIT_LAYER_IDS.surveyedAreasSelectedOutline, filter);
        };

        register();
        mlMap.on('style.load', register);

        return () => {
            mlMap.off('style.load', register);
        };
    }, [isMapLoaded, mapRef]);

    // Update selection highlight filter whenever selectedAreaName changes.
    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;
        const filter: FilterSpecification = ['==', ['get', 'name'], selectedAreaName ?? ''];
        if (mlMap.getLayer(ORBIT_LAYER_IDS.surveyedAreasSelectedFill))
            mlMap.setFilter(ORBIT_LAYER_IDS.surveyedAreasSelectedFill, filter);
        if (mlMap.getLayer(ORBIT_LAYER_IDS.surveyedAreasSelectedOutline))
            mlMap.setFilter(ORBIT_LAYER_IDS.surveyedAreasSelectedOutline, filter);
    }, [selectedAreaName, isMapLoaded, mapRef]);

    return { getAreaGeometry, getAreasData, areasDataVersion };
}
